/**
 * Settlement API to Entity Mappers
 * Maps API responses to internal entity models
 */

import { API } from '../api_clients';
import {
  Settlement,
  SettlementSummary,
  SettlementItem,
  SettlementDriverSummary,
  SettlementFinancialSummary,
  SettlementLoadSummary,
  DriverSettlementPendingSummary,
  DriverSettlementStats,
} from '../../types/settlement';
import { SettlementStatus } from '../../types/enums';

// Status mapping (case-insensitive)
const settlementStatusMap: Record<string, SettlementStatus> = {
  incomplete: SettlementStatus.INCOMPLETE,
  generated: SettlementStatus.GENERATED,
  reviewed: SettlementStatus.REVIEWED,
  paid: SettlementStatus.PAID,
  voided: SettlementStatus.VOIDED,
};

function mapSettlementStatus(status?: string): SettlementStatus {
  if (!status) return SettlementStatus.INCOMPLETE;
  return settlementStatusMap[status.toLowerCase()] || SettlementStatus.INCOMPLETE;
}

/**
 * Map API load summary to entity
 */
interface ApiLoadSummary {
  unid?: string;
  load_number?: string;
  status?: string;
  origin_city?: string;
  origin_state?: string;
  destination_city?: string;
  destination_state?: string;
  pickup_date?: string;
  delivery_date?: string;
  total_miles?: number;
}

export function mapApiLoadSummaryToEntity(
  apiLoadSummary?: ApiLoadSummary | null
): SettlementLoadSummary | null {
  if (!apiLoadSummary) return null;

  return {
    id: apiLoadSummary.unid || '',
    loadNumber: apiLoadSummary.load_number || '',
    status: apiLoadSummary.status || '',
    originCity: apiLoadSummary.origin_city || '',
    originState: apiLoadSummary.origin_state || '',
    destinationCity: apiLoadSummary.destination_city || '',
    destinationState: apiLoadSummary.destination_state || '',
    pickupDate: apiLoadSummary.pickup_date || '',
    deliveryDate: apiLoadSummary.delivery_date || '',
    totalMiles: apiLoadSummary.total_miles || 0,
  };
}

/**
 * Map API settlement item to entity
 */
export function mapApiSettlementItemToEntity(
  apiItem: API.ResponsesSettlementItem
): SettlementItem {
  return {
    type: apiItem.type || '',
    category: apiItem.category || '',
    description: apiItem.description || '',
    loadId: apiItem.load_id || null,
    loadNumber: apiItem.load_number || null,
    quantity: apiItem.quantity ?? null,
    rate: apiItem.rate ?? null,
    amount: apiItem.amount || 0,
    date: apiItem.date || null,
  };
}

/**
 * Map API settlement driver summary to entity
 */
export function mapApiSettlementDriverToEntity(
  apiDriver?: API.ResponsesSettlementDriverSummary
): SettlementDriverSummary | undefined {
  if (!apiDriver) return undefined;

  return {
    id: apiDriver.unid || '',
    firstName: apiDriver.first_name || '',
    lastName: apiDriver.last_name || '',
    payType: apiDriver.pay_type || '',
    payRate: apiDriver.pay_rate || 0,
  };
}

/**
 * Map API settlement financial summary to entity
 */
export function mapApiSettlementSummaryToFinancial(
  apiSummary?: API.ResponsesSettlementFinancialSummary
): SettlementFinancialSummary {
  if (!apiSummary) {
    return {
      grossEarnings: 0,
      loadPay: 0,
      accessorialPay: 0,
      bonusPay: 0,
      totalDeductions: 0,
      totalReimbursements: 0,
      netPay: 0,
    };
  }

  return {
    grossEarnings: apiSummary.gross_earnings || 0,
    loadPay: apiSummary.load_pay || 0,
    accessorialPay: apiSummary.accessorial_pay || 0,
    bonusPay: apiSummary.bonus_pay || 0,
    totalDeductions: apiSummary.total_deductions || 0,
    totalReimbursements: apiSummary.total_reimbursements || 0,
    netPay: apiSummary.net_pay || 0,
  };
}

/**
 * Map API settlement to full entity settlement
 */
export function mapApiSettlementToEntity(
  apiSettlement: API.ResponsesSettlement
): Settlement {
  // Use load summary dates as period fallback if available
  const loadSummary = apiSettlement.load_summary;

  return {
    // IDs
    id: apiSettlement.unid || '',
    settlementNumber: apiSettlement.settlement_number || '',
    driverId: apiSettlement.driver_id || '',
    driver: mapApiSettlementDriverToEntity(apiSettlement.driver),

    // Period - derive from load summary if available
    periodStart: loadSummary?.pickup_date || '',
    periodEnd: loadSummary?.delivery_date || '',

    // Status
    status: mapSettlementStatus(apiSettlement.status),

    // Financial summary
    summary: mapApiSettlementSummaryToFinancial(apiSettlement.summary),

    // Line items
    earningsItems: (apiSettlement.earnings_items || []).map(mapApiSettlementItemToEntity),
    deductionItems: (apiSettlement.deduction_items || []).map(mapApiSettlementItemToEntity),
    reimbursementItems: (apiSettlement.reimbursement_items || []).map(mapApiSettlementItemToEntity),

    // Totals - calculate from items if not provided
    loadCount: 1, // Single load settlement
    totalMiles: 0, // Not provided by API

    // Approval
    approvedBy: apiSettlement.approved_by || null,
    approvedAt: apiSettlement.approved_at || null,

    // Payment - not provided by API
    paymentMethod: null,
    paymentRef: null,
    paidAt: null,

    // Notes - not provided by API
    notes: null,
    driverNotes: null,

    // Metadata
    createdAt: apiSettlement.createdAt || new Date().toISOString(),
    updatedAt: apiSettlement.updatedAt || new Date().toISOString(),
    organizationId: '', // Will be set from context

    // Additional - not provided by API
    pdfUrl: null,
  };
}

/**
 * Map API settlement list summary to entity (for list views)
 */
export function mapApiSettlementListSummaryToEntity(
  apiSettlement: API.ResponsesSettlementListSummary
): SettlementSummary {
  // Cast to access additional fields not in the generated type
  const extendedSettlement = apiSettlement as API.ResponsesSettlementListSummary & {
    load_number?: string;
    load_summary?: ApiLoadSummary;
  };

  // Get load number from load_summary if available
  const loadNumber = extendedSettlement.load_summary?.load_number || extendedSettlement.load_number || null;

  return {
    id: apiSettlement.unid || '',
    settlementNumber: apiSettlement.settlement_number || '',
    driverId: apiSettlement.driver_id || '',
    driverName: apiSettlement.driver_name || null,

    // Load reference
    loadId: apiSettlement.load_id || null,
    loadNumber,
    loadSummary: mapApiLoadSummaryToEntity(extendedSettlement.load_summary),

    // Status
    status: mapSettlementStatus(apiSettlement.status),

    // Financial
    grossEarnings: apiSettlement.gross_earnings || 0,
    totalDeductions: apiSettlement.total_deductions || 0,
    netPay: apiSettlement.net_pay || 0,

    // Revision
    revision: apiSettlement.revision ?? undefined,

    // Metadata
    createdAt: apiSettlement.createdAt || new Date().toISOString(),
    updatedAt: apiSettlement.updatedAt || undefined,
    organizationId: '', // Will be set from context
  };
}

/**
 * Map API driver settlement summary to entity (from /driver-settlements/pending)
 */
export function mapApiDriverSettlementSummaryToEntity(
  apiSummary: API.ResponsesDriverSettlementSummary
): DriverSettlementPendingSummary {
  return {
    driverId: apiSummary.driver_id || '',
    driverName: apiSummary.driver_name || '',
    driverType: apiSummary.driver_type || '',
    settlementCount: apiSummary.settlement_count || 0,
    grossEarnings: apiSummary.gross_earnings || 0,
    loadPay: apiSummary.load_pay || 0,
    accessorialPay: apiSummary.accessorial_pay || 0,
    bonusPay: apiSummary.bonus_pay || 0,
    totalDeductions: apiSummary.total_deductions || 0,
    totalReimbursements: apiSummary.total_reimbursements || 0,
    netPay: apiSummary.net_pay || 0,
  };
}

/**
 * Map API driver settlement stats to entity (from /driver-settlements/stats)
 */
export function mapApiDriverSettlementStatsToEntity(
  apiStats: API.ResponsesDriverSettlementStatsResponseData
): DriverSettlementStats {
  return {
    pending: {
      settlementCount: apiStats.pending?.settlement_count || 0,
      totalNetPay: apiStats.pending?.total_net_pay || 0,
    },
  };
}
